import React, { useEffect, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import {
  getIntrlockType, intrlockTypeLabel, mapIntrlockTypeToClass,
  deviceTypeIcon, IntrlockType,
  getUniformState, getUniformOnline, getUniformCapabilities,
} from '../access/DeviceVisuals';
import { adaptDeviceStatusResponse, DeviceLiveState } from '../../utils/deviceLiveState';

const INTRLOCK_TYPES: IntrlockType[] = [
  'Lock', 'Siren', 'Speaker', 'Audio Bridge', 'Camera', 'Sensor', 'Light', 'Relay', 'Device',
];

interface SystemContext {
  acs_system?: { id?: string | null; name?: string | null; provider?: string | null; status?: string | null };
  cloud_node?: { id?: string | null; name?: string | null; status?: string | null; serial?: string | null; connected?: boolean | null; software_version?: string | null };
  property?: { id?: string | null; name?: string | null } | null;
}
const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
const tok = () =>
  localStorage.getItem('intrlock_token') ||
  localStorage.getItem('jwt') ||
  localStorage.getItem('token') ||
  '';

interface PersonRow {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  person_type?: string | null;
  unit_number?: string | null;
  property_name?: string | null;
  revoked?: boolean;
}
interface UnlockEvent {
  id: string;
  occurred_at: string;
  event_type?: string;
  method?: string | null;
  success?: boolean;
  reason?: string | null;
  person_id?: string | null;
  person_name?: string | null;
  unit_number?: string | null;
  device_name?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Device row id from the Devices page — may be `acs-<uuid>` for ACS rows. */
  device: { id: string | number; name?: string; type?: string; device_type?: string; property_id?: string | number; pdk_device_id?: string; acs_provider?: string; canonical_device_class?: string; canonical_type_detected?: string; mfg?: string } | null;
  /** Called after a successful PATCH so the parent list can refetch. */
  onDeviceUpdated?: () => void;
}

const fmtName = (a?: string | null, b?: string | null) =>
  `${a ?? ''} ${b ?? ''}`.trim() || '—';

const formatDateTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const methodLabel: Record<string, string> = {
  app: 'Mobile App',
  dtmf: 'DTMF',
  pin: 'PIN code',
  face: 'Face',
  face_recognition: 'Face',
  remote: 'Remote',
  guest_pin: 'Guest PIN',
  ble: 'Bluetooth',
};

export const DeviceDetailModal: React.FC<Props> = ({ open, onClose, device, onDeviceUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [withAccess, setWithAccess] = useState<PersonRow[]>([]);
  const [withoutAccess, setWithoutAccess] = useState<PersonRow[]>([]);
  const [events, setEvents] = useState<UnlockEvent[]>([]);
  const [tab, setTab] = useState<'access' | 'history'>('access');
  const [accessOpen, setAccessOpen] = useState(true);
  const [withoutOpen, setWithoutOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busyPerson, setBusyPerson] = useState<string | null>(null);

  // Editable fields
  const initialName = String(device?.name ?? '');
  const initialType: IntrlockType = getIntrlockType(device);
  const [editName, setEditName] = useState(initialName);
  const [editType, setEditType] = useState<IntrlockType>(initialType);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // System context + live state
  const [sysCtx, setSysCtx] = useState<SystemContext | null>(null);
  const [live, setLive] = useState<DeviceLiveState | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveErr, setLiveErr] = useState<string | null>(null);
  const [liveAgeTick, setLiveAgeTick] = useState(0);
  const liveTimer = useRef<any>(null);
  const ageTimer = useRef<any>(null);

  useEffect(() => {
    if (!open || !device) return;
    let cancelled = false;
    const idStr = String(device.id);
    setLoading(true);
    setError(null);
    setWithAccess([]);
    setWithoutAccess([]);
    setEvents([]);
    const headers = { Authorization: `Bearer ${tok()}` };
    (async () => {
      try {
        const rosterPromise = fetch(
          `${API}/acs-systems/devices/${encodeURIComponent(idStr)}/access-roster`,
          { headers },
        )
          .then(r => r.ok ? r.json() : { with_access: [], without_access: [] })
          .catch(() => ({ with_access: [], without_access: [] }));
        const auditPromise = fetch(
          `${API}/acs-systems/devices/${encodeURIComponent(idStr)}/unlock-audit?limit=200`,
          { headers },
        )
          .then(r => r.ok ? r.json() : { items: [] })
          .catch(() => ({ items: [] }));
        const [roster, audit] = await Promise.all([rosterPromise, auditPromise]);
        if (cancelled) return;
        setWithAccess(roster.with_access ?? []);
        setWithoutAccess(roster.without_access ?? []);
        setEvents(audit.items ?? []);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, device]);

  // Reset edit state when device changes
  useEffect(() => {
    setEditName(String(device?.name ?? ''));
    setEditType(getIntrlockType(device));
    setSaveErr(null);
  }, [device?.id]);
  const idStrForLoad = device ? String(device.id) : '';
  const isAcsForLoad = idStrForLoad.startsWith('acs-');
  const nativeDeviceIdForLoad = isAcsForLoad ? '' : idStrForLoad;

  // Load system-context + live state (only for native devices — /devices/:id)
  useEffect(() => {
    if (!open || !device) return;
    // Skip acs-prefixed rows — they're not on the /devices table
    if (!nativeDeviceIdForLoad) { setSysCtx(null); setLive(null); return; }
    let cancelled = false;
    const headers = { Authorization: `Bearer ${tok()}` };
    (async () => {
      try {
        const r = await fetch(`${API}/devices/${encodeURIComponent(nativeDeviceIdForLoad)}/system-context`, { headers });
        if (!cancelled && r.ok) setSysCtx(await r.json());
      } catch {}
    })();
    fetchLive(nativeDeviceIdForLoad);
    if (liveTimer.current) clearInterval(liveTimer.current);
    liveTimer.current = setInterval(() => fetchLive(nativeDeviceIdForLoad), 30_000);
    if (ageTimer.current) clearInterval(ageTimer.current);
    ageTimer.current = setInterval(() => setLiveAgeTick(t => t + 1), 1_000);
    return () => {
      cancelled = true;
      if (liveTimer.current) clearInterval(liveTimer.current);
      if (ageTimer.current) clearInterval(ageTimer.current);
    };
  }, [open, nativeDeviceIdForLoad]);
  const fetchLive = async (nativeId: string) => {
    if (!nativeId) return;
    setLiveLoading(true);
    setLiveErr(null);
    try {
      const r = await fetch(`${API}/devices/${encodeURIComponent(nativeId)}/status`, { headers: { Authorization: `Bearer ${tok()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setLive(adaptDeviceStatusResponse(j));
    } catch (e: any) {
      setLiveErr(String(e?.message ?? e));
    } finally {
      setLiveLoading(false);
    }
  };

  const dirty = !!device && (editName !== initialName || editType !== initialType);
  const saveChanges = async () => {
    if (!device || !nativeDeviceIdForLoad) return;
    setSaving(true); setSaveErr(null);
    try {
      const r = await fetch(`${API}/devices/${encodeURIComponent(nativeDeviceIdForLoad)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({
          name: editName,
          canonical_device_class: mapIntrlockTypeToClass(editType),
        }),
      });
      if (!r.ok) throw new Error((await r.json())?.error || `HTTP ${r.status}`);
      onDeviceUpdated?.();
    } catch (e: any) {
      setSaveErr(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  };
  const discardChanges = () => {
    setEditName(initialName);
    setEditType(initialType);
    setSaveErr(null);
  };

  if (!device) return null;
  const idStr = String(device.id);
  const isAcs = idStr.startsWith('acs-');
  const acsDeviceId = isAcs ? idStr.slice(4) : idStr;

  const ageSecs = live?.fetched_at ? Math.max(0, Math.floor((Date.now() - new Date(live.fetched_at).getTime()) / 1000)) : null;
  void liveAgeTick; // used to re-render age string

  const reload = async () => {
    const headers = { Authorization: `Bearer ${tok()}` };
    const r = await fetch(`${API}/acs-systems/devices/${encodeURIComponent(idStr)}/access-roster`, { headers });
    if (r.ok) {
      const j = await r.json();
      setWithAccess(j.with_access ?? []);
      setWithoutAccess(j.without_access ?? []);
    }
  };

  const toggleAccess = async (p: PersonRow, ctx: 'with' | 'without') => {
    if (!isAcs) {
      alert('Editing access for this device type is not supported yet — only access-control system devices can have per-tenant grants/revokes.');
      return;
    }
    const action = ctx === 'with' ? 'revoke' : 'restore';
    setBusyPerson(p.id);
    try {
      const res = await fetch(`${API}/acs-systems/people/${encodeURIComponent(p.id)}/revocations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` },
        body: JSON.stringify({ acs_device_id: acsDeviceId, action }),
      });
      if (!res.ok) throw new Error(await res.text());
      await reload();
    } catch (e: any) {
      alert(`Failed to ${action} access: ${e?.message ?? e}`);
    } finally {
      setBusyPerson(null);
    }
  };

  const PersonRowEl = (p: PersonRow, ctx: 'with' | 'without') => (
    <div key={p.id} className="flex items-center justify-between px-3 py-2 border-b border-gray-50 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{fmtName(p.first_name, p.last_name)}</p>
        <p className="text-xs text-gray-500 truncate">
          {p.unit_number ? `Unit ${p.unit_number}` : ''}
          {p.unit_number && p.property_name ? ' · ' : ''}
          {p.property_name || ''}
          {(p.email || p.phone) ? ' · ' : ''}
          {p.email || p.phone || ''}
        </p>
      </div>
      <div className="flex-shrink-0 ml-3 flex items-center gap-2">
        {ctx === 'with'
          ? <Badge color="green">Has access</Badge>
          : p.revoked
            ? <Badge color="red">Revoked</Badge>
            : <Badge color="gray">No access</Badge>}
        {editing && (
          <button
            onClick={() => toggleAccess(p, ctx)}
            disabled={busyPerson === p.id}
            className={`text-[11px] font-semibold px-2 py-1 rounded border disabled:opacity-50 ${
              ctx === 'with'
                ? 'border-red-200 text-red-700 hover:bg-red-50'
                : 'border-green-200 text-green-700 hover:bg-green-50'
            }`}
          >
            {busyPerson === p.id ? '…' : ctx === 'with' ? 'Remove' : (p.revoked ? 'Restore' : 'Grant')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={device.name || 'Device'} size="lg">
      <div className="space-y-4">
        {/* Editable header — name + Intrlock canonical type */}
        {!isAcs && (
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
            <div className="flex items-center gap-3">
              {deviceTypeIcon({ ...(device as any), canonical_device_class: mapIntrlockTypeToClass(editType) })}
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={200}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-1">Device Type</label>
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value as IntrlockType)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    {INTRLOCK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>
            {dirty && (
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
                {saveErr && <span className="text-xs text-red-600 mr-2">{saveErr}</span>}
                <button onClick={discardChanges} disabled={saving} className="px-3 py-1.5 text-xs font-semibold border border-gray-300 rounded hover:bg-white disabled:opacity-50">Discard</button>
                <button onClick={saveChanges} disabled={saving} className="px-3 py-1.5 text-xs font-semibold bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button>
              </div>
            )}
          </div>
        )}

        {/* System block */}
        {!isAcs && sysCtx && (sysCtx.acs_system?.id || sysCtx.cloud_node?.id) && (
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">System</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {sysCtx.acs_system?.id && (
                <div>
                  <p className="text-gray-500">ACS System</p>
                  <a href={`/access-control/${sysCtx.acs_system.id}`} className="text-brand-700 hover:underline font-medium">{sysCtx.acs_system.name || '—'}</a>
                  {sysCtx.acs_system.provider && <p className="text-[10px] text-gray-400 mt-0.5">{sysCtx.acs_system.provider}</p>}
                </div>
              )}
              {sysCtx.cloud_node?.name && (
                <div>
                  <p className="text-gray-500">Cloud Node</p>
                  <p className="font-medium text-gray-800">{sysCtx.cloud_node.name}</p>
                  <div className="mt-0.5">
                    {sysCtx.cloud_node.connected === true
                      ? <Badge color="green">Connected</Badge>
                      : sysCtx.cloud_node.connected === false
                        ? <Badge color="red">Disconnected</Badge>
                        : <Badge color="gray">Unknown</Badge>}
                  </div>
                </div>
              )}
              {sysCtx.cloud_node?.software_version && (
                <div>
                  <p className="text-gray-500">Software</p>
                  <p className="font-medium text-gray-800">{sysCtx.cloud_node.software_version}</p>
                </div>
              )}
              {sysCtx.property?.id && (
                <div>
                  <p className="text-gray-500">Property</p>
                  <p className="font-medium text-gray-800">{sysCtx.property.name || '—'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* §28.518.79 Wave U: Uniform shape status — shown when __intrlock is present */}
        {device && (device as any).__intrlock && !live && (
          <div className="border border-blue-100 bg-blue-50 rounded-lg p-3">
            <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide mb-2">Device Status (Uniform)</p>
            <div className="flex flex-wrap gap-2 items-center text-xs">
              {(() => {
                const uState = getUniformState(device);
                const uOnline = getUniformOnline(device);
                return (
                  <>
                    {uOnline === true && <Badge color="green">Online</Badge>}
                    {uOnline === false && <Badge color="red">Offline</Badge>}
                    {uState.locked === true && <Badge color="green">Locked</Badge>}
                    {uState.locked === false && <Badge color="yellow">Unlocked</Badge>}
                    {uState.doorOpen === true && <Badge color="yellow">Door open</Badge>}
                    {uState.alarm === true && <Badge color="red">Alarm</Badge>}
                    {uState.battery_pct != null && <span className="text-gray-500">Battery {uState.battery_pct}%</span>}
                    {uState.firmware_version && <span className="text-gray-500">fw {uState.firmware_version}</span>}
                  </>
                );
              })()}
            </div>
            {/* vendor_metadata k/v bag */}
            {(device as any).__intrlock?.vendor_metadata && Object.keys((device as any).__intrlock.vendor_metadata).length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                {Object.entries((device as any).__intrlock.vendor_metadata as Record<string, unknown>)
                  .filter(([, v]) => v != null)
                  .map(([k, v]) => (
                    <React.Fragment key={k}>
                      <span className="text-gray-500">{k.replace(/_/g, ' ')}</span>
                      <span className="text-gray-800 font-medium truncate">{String(v)}</span>
                    </React.Fragment>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Live state block */}
        {!isAcs && (
          <div className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Live State</p>
              <div className="flex items-center gap-2">
                {ageSecs != null && <span className="text-[10px] text-gray-400">last fetched {ageSecs}s ago</span>}
                <button onClick={() => fetchLive(nativeDeviceIdForLoad)} disabled={liveLoading} className="text-[11px] font-semibold px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                  {liveLoading ? '…' : 'Refresh live'}
                </button>
              </div>
            </div>
            {liveErr ? (
              <p className="text-xs text-red-600">{liveErr}</p>
            ) : !live ? (
              <p className="text-xs text-gray-400 italic">{liveLoading ? 'Loading live state…' : 'No live state available.'}</p>
            ) : (
              <div className="flex flex-wrap gap-2 items-center text-xs">
                {live.communicationLost === true
                  ? <Badge color="red">Comm lost</Badge>
                  : <Badge color="green">Online</Badge>}
                {live.locked === true && <Badge color="green">Locked</Badge>}
                {live.locked === false && <Badge color="yellow">Unlocked</Badge>}
                {live.dpsOpen === true && <Badge color="yellow">Door open</Badge>}
                {live.dpsOpen === false && <Badge color="gray">Door closed</Badge>}
                {live.relayOpen === true && <Badge color="yellow">Relay open</Badge>}
                {live.relayOpen === false && <Badge color="gray">Relay closed</Badge>}
                {live.rexOn === true && <Badge color="blue">REX active</Badge>}
                {live.firmware_version && <span className="text-gray-500">fw {live.firmware_version}</span>}
                {live.observed_at && <span className="text-gray-500">observed {formatDateTime(live.observed_at)}</span>}
                {live.unavailable_reason && <span className="w-full text-amber-700">Provider detail unavailable: {live.unavailable_reason}</span>}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {intrlockTypeLabel(device)}
            {device.acs_provider && <> · {String(device.acs_provider).toUpperCase()}</>}
          </div>
          <div className="flex items-center gap-2">
            {tab === 'access' && (
              <button
                onClick={() => setEditing((v) => !v)}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded border ${editing ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                title={isAcs ? 'Edit per-tenant access for this device' : 'Edit access only available for ACS-backed devices'}
              >
                {editing ? 'Done' : 'Edit access'}
              </button>
            )}
            <div className="flex border border-gray-200 rounded-md overflow-hidden text-xs">
              <button
                onClick={() => setTab('access')}
                className={`px-3 py-1.5 ${tab === 'access' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700'}`}
              >Access</button>
              <button
                onClick={() => setTab('history')}
                className={`px-3 py-1.5 border-l border-gray-200 ${tab === 'history' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700'}`}
              >Event History</button>
            </div>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">Failed to load: {error}</p>
        )}

        {tab === 'access' && (
          <>
            {loading ? (
              <p className="text-sm text-gray-400 italic">Loading…</p>
            ) : (
              <div className="space-y-2">
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setAccessOpen(v => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-green-50 border-b border-green-100 text-sm font-semibold text-green-800"
                  >
                    <span>Tenants with access ({withAccess.length})</span>
                    <span className="text-xs">{accessOpen ? '▾' : '▸'}</span>
                  </button>
                  {accessOpen && (
                    withAccess.length === 0
                      ? <p className="px-3 py-3 text-xs text-gray-400 italic">— no one currently has access —</p>
                      : <div className="divide-y divide-gray-50">{withAccess.map((p) => PersonRowEl(p, 'with'))}</div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setWithoutOpen(v => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-700"
                  >
                    <span>Tenants without access ({withoutAccess.length})</span>
                    <span className="text-xs">{withoutOpen ? '▾' : '▸'}</span>
                  </button>
                  {withoutOpen && (
                    withoutAccess.length === 0
                      ? <p className="px-3 py-3 text-xs text-gray-400 italic">— everyone on this property has access —</p>
                      : <div className="divide-y divide-gray-50">{withoutAccess.map((p) => PersonRowEl(p, 'without'))}</div>
                  )}
                </div>

              </div>
            )}
          </>
        )}

        {tab === 'history' && (
          loading ? (
            <p className="text-sm text-gray-400 italic">Loading…</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-gray-400 italic px-2 py-3 bg-gray-50 rounded">No unlock events recorded for this device yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-[11px] text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="py-2">When</th>
                    <th className="py-2">Person</th>
                    <th className="py-2">Method</th>
                    <th className="py-2">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map((ev) => (
                    <tr key={ev.id}>
                      <td className="py-2 text-xs text-gray-700">{formatDateTime(ev.occurred_at)}</td>
                      <td className="py-2">
                        <p className="text-sm">{ev.person_name || (ev.person_id ? <span className="text-gray-500">person {String(ev.person_id).slice(0, 8)}</span> : <span className="text-gray-400">— no person —</span>)}</p>
                        {ev.unit_number && <p className="text-[11px] text-gray-500">Unit {ev.unit_number}</p>}
                      </td>
                      <td className="py-2 text-xs">
                        <Badge color="blue">{ev.method ? (methodLabel[ev.method] ?? ev.method) : '—'}</Badge>
                      </td>
                      <td className="py-2 text-xs">
                        {ev.success === false
                          ? <Badge color="red">Failed{ev.reason ? `: ${ev.reason}` : ''}</Badge>
                          : <Badge color="green">Unlocked</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </Modal>
  );
};
